/**
 * SuggestedProfilesPanel — other-profile right-rail card showing suggested profiles.
 *
 * Each row is its own SuggestedRow subcomponent (per Rules of Hooks —
 * useFollow + useRequireAuth must be called at the top level of a component,
 * not inside a .map() callback). Mirrors WhoToFollowPanel exactly.
 */
import styled from 'styled-components';
import { Panel, SectionLabel, Avatar, Button } from '@/ui';
import { useSuggestedProfiles, useFollow, useRequireAuth, followButtonLabel } from '@/hooks';
import { formatCount } from '@/components/formatCount';
import type { SuggestedProfile } from '@/core/models';

/* ── Styled helpers ─────────────────────────────────────────────────────────── */

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const Name = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Handle = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Followers = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const EmptyState = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* ── Per-row subcomponent (calls useFollow + useRequireAuth at top level) ─── */

function SuggestedRow({ profile }: { profile: SuggestedProfile }) {
  const { isFollowing, isRequested, toggleFollow } = useFollow(profile.handle);
  const requireAuth = useRequireAuth();

  function handleFollow() {
    if (!requireAuth('follow')) return;
    void toggleFollow();
  }

  return (
    <Row>
      <Avatar name={profile.name} size={36} />
      <Info>
        <Name>{profile.name}</Name>
        <Handle>{profile.handle}</Handle>
        {profile.followerCount > 0 && (
          <Followers>{formatCount(profile.followerCount)} followers</Followers>
        )}
      </Info>
      <Button
        variant={isFollowing || isRequested ? 'secondary' : 'outline'}
        onClick={handleFollow}
        aria-label={`${followButtonLabel(isFollowing, isRequested)} ${profile.handle}`}
      >
        {followButtonLabel(isFollowing, isRequested)}
      </Button>
    </Row>
  );
}

/* ── Panel ────────────────────────────────────────────────────────────────── */

export function SuggestedProfilesPanel() {
  const profiles = useSuggestedProfiles();

  return (
    <Panel>
      <SectionLabel as="h2">Suggested profiles</SectionLabel>

      {profiles.length === 0 ? (
        <EmptyState>No suggestions available.</EmptyState>
      ) : (
        <List>
          {profiles.map((profile) => (
            <SuggestedRow key={profile.handle} profile={profile} />
          ))}
        </List>
      )}
    </Panel>
  );
}
